import React, { forwardRef } from 'react';
import ReactDatePicker, { DatePickerProps as ReactDatePickerProps } from 'react-datepicker';
import { useInputCommon } from '../../hooks';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './DatePicker.module.scss';

export type DatePickerProps = {
    label?: string;
    error?: string;
    helperText?: string;
    className?: string;
    wrapperClassName?: string;

    // Single date selection props
    selected?: Date | null;
    onChange?: (date: Date | null) => void;

    // Range selection props
    selectsRange?: boolean;
    startDate?: Date | null;
    endDate?: Date | null;
    onRangeChange?: (dates: [Date | null, Date | null]) => void;

    // Common props
    placeholderText?: string;
    dateFormat?: string;
    id?: string;
    required?: boolean;
    disabled?: boolean;
    onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    [key: string]: any;
};

export const DatePicker = forwardRef<ReactDatePicker, DatePickerProps>(
    (
        {
            label,
            error,
            helperText,
            className,
            wrapperClassName,
            id,
            required,
            disabled,
            onFocus,
            onBlur,

            // Single date props
            selected,
            onChange,

            // Range selection props
            selectsRange,
            startDate,
            endDate,
            onRangeChange,

            ...datePickerProps
        },
        ref
    ) => {
        const { labelElement, errorTextElement, helperTextElement, containerClasses } = useInputCommon({
            id,
            label,
            error: !!error,
            errorText: error,
            helperText,
            required: !!required,
            disabled: !!disabled,
            className: wrapperClassName,
            inputType: 'datepicker',
        });

        const datePickerClassName = `${styles.datePicker} ${error ? styles.error : ''} ${className || ''}`.trim();
        const wrapperClass = styles.datePickerWrapper;

        const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            if (onFocus) {
                onFocus(e);
            }
        };

        const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            if (onBlur) {
                onBlur(e);
            }
        };

        // Handle change for both single date and range selection
        const handleChange = (date: Date | null | [Date | null, Date | null]) => {
            if (selectsRange && onRangeChange) {
                // Range selection: date will be an array [startDate, endDate]
                onRangeChange(date as [Date | null, Date | null]);
            } else if (!selectsRange && onChange) {
                // Single date selection: date will be a single Date or null
                onChange(date as Date | null);
            }
        };

        // Prepare props for ReactDatePicker
        const reactDatePickerProps: ReactDatePickerProps = {
            id,
            className: datePickerClassName,
            wrapperClassName: wrapperClass,
            disabled,
            onFocus: handleFocus,
            onBlur: handleBlur,
            onChange: handleChange,
            ...datePickerProps,
        };

        // Add range-specific or single-date-specific props
        if (selectsRange) {
            Object.assign(reactDatePickerProps, {
                selectsRange: true,
                startDate,
                endDate,
            });
        } else {
            Object.assign(reactDatePickerProps, {
                selected,
            });
        }

        return (
            <div className={containerClasses}>
                {labelElement}
                <ReactDatePicker ref={ref} {...reactDatePickerProps} />
                {errorTextElement}
                {helperTextElement}
            </div>
        );
    }
);

DatePicker.displayName = 'DatePicker';
